import { useEffect, useState } from "react";
import { GetStatisticsUniversityGenderConfig } from "src/service/api";
import { StatisticsUniversityGenderTypes } from "src/static/interface";

export interface UniversityData {
  name: string;
  percentage: number;
  total: number;
  students: number;
  completed: number;
}

export interface GenderDistributionData {
  name: string;
  value: number;
  color: string;
  count: number;
}

export const useDashboardStatistics = (
  selectedQuizRuleId: number | null,
  universityCode?: string,
  isCompleted?: boolean
) => {
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [genderDistributionData, setGenderDistributionData] = useState<
    GenderDistributionData[]
  >([]);
  const [top10ByPercentage, setTop10ByPercentage] = useState<UniversityData[]>(
    []
  );
  const [bottom10ByPercentage, setBottom10ByPercentage] = useState<
    UniversityData[]
  >([]);
  const [top10ByCompleted, setTop10ByCompleted] = useState<UniversityData[]>(
    []
  );
  const [bottom10ByCompleted, setBottom10ByCompleted] = useState<
    UniversityData[]
  >([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [totalStudents, setTotalStudents] = useState<number>(0);
  const [totalNotCompleted, setTotalNotCompleted] = useState<number>(0);
  const [maleUsers, setMaleUsers] = useState<number>(0);
  const [femaleUsers, setFemaleUsers] = useState<number>(0);
  const [participatedUniversitiesCount, setParticipatedUniversitiesCount] = useState<number>(0);
  const [totalUniversitiesCount, setTotalUniversitiesCount] = useState<number>(0);

  // Helper function to process API data
  const processData = (data: StatisticsUniversityGenderTypes[]) => {
    const validData = data.filter(
      (item) =>
        item.participationRate != null &&
        !isNaN(item.participationRate) &&
        item.participationRate >= 0
    );

    let totalCompleted = 0;
    let totalStudentsCount = 0;
    let totalMaleCompleted = 0;
    let totalFemaleCompleted = 0;

    validData.forEach((item) => {
      totalCompleted += item.participantTotal;
      totalStudentsCount += item.total;
      totalMaleCompleted += item.participantMale;
      totalFemaleCompleted += item.participantFemale;
    });

    const participatedUniversities = validData.filter(
      (item) => item.participantTotal > 0
    ).length;

    const allData = validData.map((item) => {
      return {
        name: item.university,
        percentage: item.participationRate,
        total: item.total,
        students: item.total,
        completed: item.participantTotal,
      };
    });

    return {
      validData,
      totalCompleted,
      totalStudentsCount,
      totalMaleCompleted,
      totalFemaleCompleted,
      participatedUniversities,
      totalUniversities: validData.length,
      allData,
    };
  };

  // Bitta effect: universityCode bo'lmasa — bitta so'rov (Top/Bottom + stats); bo'lsa — 2 ta parallel, ikkalasi tugagach state yangilanadi
  useEffect(() => {
    if (!selectedQuizRuleId) return;

    setLoadingCharts(true);

    const baseParams = {
      quizRuleId: selectedQuizRuleId,
      ...(isCompleted !== undefined && { isCompleted }),
    };

    const applyTopBottom = (data: StatisticsUniversityGenderTypes[]) => {
      const { allData } = processData(data);
      const sortedByPercentageDesc = [...allData].sort((a, b) => b.percentage - a.percentage);
      const sortedByPercentageAsc = [...allData].sort((a, b) => a.percentage - b.percentage);
      const sortedByCompletedDesc = [...allData].sort((a, b) => b.completed - a.completed);
      const sortedByCompletedAsc = [...allData].sort((a, b) => a.completed - b.completed);
      setTop10ByPercentage(sortedByPercentageDesc.slice(0, 10));
      setBottom10ByPercentage(sortedByPercentageAsc.slice(0, 10));
      setTop10ByCompleted(sortedByCompletedDesc.slice(0, 10));
      setBottom10ByCompleted(sortedByCompletedAsc.slice(0, 10));
    };

    const applyStats = (data: StatisticsUniversityGenderTypes[]) => {
      const {
        totalCompleted,
        totalStudentsCount,
        totalMaleCompleted,
        totalFemaleCompleted,
        participatedUniversities,
        totalUniversities,
      } = processData(data);
      setParticipatedUniversitiesCount(participatedUniversities);
      setTotalUniversitiesCount(totalUniversities);
      setTotalUsers(totalCompleted);
      setTotalStudents(totalStudentsCount);
      setTotalNotCompleted(totalStudentsCount - totalCompleted);
      setMaleUsers(totalMaleCompleted);
      setFemaleUsers(totalFemaleCompleted);
      const malePercent = totalCompleted > 0 ? (totalMaleCompleted / totalCompleted) * 100 : 0;
      const femalePercent = totalCompleted > 0 ? (totalFemaleCompleted / totalCompleted) * 100 : 0;
      setGenderDistributionData([
        { name: "Erkaklar", value: Math.round(malePercent * 10) / 10, color: "#4facfe", count: totalMaleCompleted },
        { name: "Ayollar", value: Math.round(femalePercent * 10) / 10, color: "#f093fb", count: totalFemaleCompleted },
      ]);
    };

    const resetAll = () => {
      setTop10ByPercentage([]);
      setBottom10ByPercentage([]);
      setTop10ByCompleted([]);
      setBottom10ByCompleted([]);
      setTotalUsers(0);
      setTotalStudents(0);
      setTotalNotCompleted(0);
      setMaleUsers(0);
      setFemaleUsers(0);
      setParticipatedUniversitiesCount(0);
      setTotalUniversitiesCount(0);
      setGenderDistributionData([]);
    };

    if (!universityCode) {
      GetStatisticsUniversityGenderConfig(baseParams)
        .then((res) => {
          const data: StatisticsUniversityGenderTypes[] = res.data || [];
          applyTopBottom(data);
          applyStats(data);
        })
        .catch(resetAll)
        .finally(() => setLoadingCharts(false));
      return;
    }

    Promise.all([
      GetStatisticsUniversityGenderConfig(baseParams),
      GetStatisticsUniversityGenderConfig({ ...baseParams, universityCode }),
    ])
      .then(([resTopBottom, resStats]) => {
        applyTopBottom(resTopBottom.data || []);
        applyStats(resStats.data || []);
      })
      .catch(resetAll)
      .finally(() => setLoadingCharts(false));
  }, [selectedQuizRuleId, universityCode, isCompleted]);

  return {
    loadingCharts,
    genderDistributionData,
    top10ByPercentage,
    bottom10ByPercentage,
    top10ByCompleted,
    bottom10ByCompleted,
    totalUsers,
    totalStudents,
    totalNotCompleted,
    maleUsers,
    femaleUsers,
    participatedUniversitiesCount,
    totalUniversitiesCount,
  };
};

